import React, { Fragment } from 'react';
import { createActions, handleActions } from 'redux-actions';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import { createSelector } from 'reselect';
import round from 'lodash/round';
import omit from 'lodash/omit';
import uuid from 'uuid/v4';

// Api
import { migrateDebts, updateDebt, removeDebt, fetchLocalDebts, saveLocalDebts, saveAllDebts } from 'lib/api';

// Modules
import { actions as modalActions } from 'modules/modal';
import { actions as toastActions } from 'modules/toast';
import { actions as userActions, selectors as userSelectors } from 'modules/user';

// Utils
import { replaceItem, removeItem } from 'utils/array';
import { add, divide, subtract, toNumber } from 'utils/number';

// Constants
import DEBT_CONTRACT from 'constants/debtContract';
import DEFAULT_DEBTS from 'constants/debts';

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

// State
const defaultState = {
  debts: [],
  debtsCache: [],
  loading: true,
};

// Selectors
export const selectors = {
  debts: state => state.debts.debts,
  loading: state => state.debts.loading,
  startingBalance: state => state.debts.debts.reduce((a, d) => add(a, d.starting), 0),
  totalBalance: state => state.debts.debts.reduce((a, d) => add(a, d.current), 0),
};

selectors.totalPaid = createSelector(
  [selectors.startingBalance, selectors.totalBalance],
  (startingBalance, totalBalance) => {
    return subtract(startingBalance, totalBalance);
  }
);

selectors.currentProgress = createSelector(
  [selectors.startingBalance, selectors.totalBalance, selectors.totalPaid],
  (startingBalance, totalBalance, totalPaid) => {
    const startingNum = toNumber(startingBalance, true);
    const percentage = startingNum > 0 ? divide(totalPaid, startingNum) * 100 : 0;
    const progress = round(percentage, 2);
    return progress >= 0 ? progress : 0;
  }
);

// Actions
export const actions = createActions(
  {
    ADD_INTEREST: (debt, amt) => ({ debt, amt }),
    ADD_PAYMENT: (debt, amt) => ({ debt, amt: -amt }),
    SAVE_DEBT: (debt, muted) => ({ debt, muted }),
  },
  'ADD_DEBT',
  'DELETE_DEBT',
  'REMOVE_DEBT',
  'UPSERT_DEBT',
  'LOADED_DEBTS',
  'REORDER_DEBTS',
  'REPLACE_DEBTS'
);

// Normalizers
const normalizeDebt = ({ current, dollarSpecificity, starting, interest, order, ...debt }) => {
  return {
    dollarSpecificity: toNumber(dollarSpecificity),
    current: toNumber(current),
    starting: toNumber(starting),
    interest: toNumber(interest),
    order: toNumber(order),
    ...debt,
  };
};

// Sagas
export function* updateBalanceSaga({ payload: { debt: initialDebt, amt } }) {
  try {
    const { current, starting, ...debt } = initialDebt;
    const newCurrent = round(add(current, amt), 2);

    yield put(
      actions.saveDebt(
        {
          current: newCurrent,
          starting: newCurrent > starting ? newCurrent : starting,
          ...debt,
        },
        true
      )
    );

    if (newCurrent <= 0) {
      yield put(
        modalActions.showModal('debtPaid', {
          debt: initialDebt,
        })
      );
    } else if (amt < 0) {
      // Payment
      yield put(
        toastActions.createToast('success', {
          timeout: 6000,
          title: `${initialDebt.name} - Payment`,
          message: (
            <Fragment>
              <p>
                Payment of <strong>{formatter.format(amt * -1)}</strong> added successfully!
              </p>
              <p>
                Your new balance is <strong>{formatter.format(newCurrent, 2)}</strong>
              </p>
            </Fragment>
          ),
        })
      );
    } else if (amt > 0) {
      // Interest
      yield put(
        toastActions.createToast('success', {
          timeout: 6000,
          title: `${initialDebt.name} - Interest`,
          message: (
            <Fragment>
              <p>
                Interest charge of <strong>{formatter.format(amt)}</strong> added successfully!
              </p>
              <p>
                Your new balance is <strong>{formatter.format(newCurrent, 2)}</strong>
              </p>
            </Fragment>
          ),
        })
      );
    }
  } catch (e) {
    yield put(
      toastActions.createToast('alert', {
        title: `${initialDebt.name} - Failure`,
        message: `Debt balance failed to save! Please try again later or refresh the page.`,
      })
    );
  }
}

export function* checkDebtsSaga({ payload: debts }) {
  try {
    if (!debts) {
      // No Debts in DB, Migrating Local Storage
      const debts = yield call(fetchLocalDebts, DEFAULT_DEBTS);
      yield call(migrateDebts, debts);
    }
  } catch (e) {
    console.error(e);
  }
}

export function* saveDebtSaga({ payload: { debt: initialDebt, muted } }) {
  try {
    const isLoggedIn = yield select(userSelectors.isLoggedIn);
    const debt = normalizeDebt(initialDebt);

    if (isLoggedIn) {
      const insert = yield call(updateDebt, debt);
      if (insert) yield put(actions.removeDebt(debt.id));
    } else {
      const currentDebts = yield select(selectors.debts);
      const omitted = omit(debt, ['isNew']);
      const debts = yield call(saveLocalDebts, replaceItem(currentDebts, omitted));
      yield put(actions.replaceDebts(debts));
    }

    if (!muted) {
      yield put(
        toastActions.createToast('success', {
          title: `${initialDebt.name} - Saved`,
          message: `Debt saved successfully!`,
        })
      );
    }
  } catch (e) {
    yield put(
      toastActions.createToast('alert', {
        title: `${initialDebt.name} - Failure`,
        message: `Debt failed to save! Please try again later or refresh the page.`,
      })
    );
  }
}

export function* deleteDebtSaga({ payload: { isNew, ...debt } }) {
  try {
    if (isNew) {
      yield put(actions.removeDebt(debt.id));
    } else {
      const isLoggedIn = yield select(userSelectors.isLoggedIn);

      if (isLoggedIn) {
        yield call(removeDebt, debt.id);
      } else {
        const currentDebts = yield select(selectors.debts);
        const debts = yield call(saveLocalDebts, removeItem(currentDebts, debt));
        yield put(actions.replaceDebts(debts));
      }
    }

    if (!isNew) {
      yield put(
        toastActions.createToast('success', {
          title: `${debt.name} - Removed`,
          message: `Debt removed successfully!`,
        })
      );
    }
  } catch (e) {
    yield put(
      toastActions.createToast('alert', {
        title: `${debt.name} - Failure`,
        message: `Debt failed to remove! Please try again later or refresh the page.`,
      })
    );
  }
}

export function* reorderDebtsSaga({ payload: debts }) {
  try {
    const isLoggedIn = yield select(userSelectors.isLoggedIn);
    const orderedDebts = debts.map((debt, i) => {
      return {
        ...debt,
        order: i,
      };
    });

    if (isLoggedIn) {
      yield call(saveAllDebts, orderedDebts);
    } else {
      const debts = yield call(saveLocalDebts, orderedDebts);
      yield put(actions.replaceDebts(debts));
    }
  } catch (e) {
    yield put(
      toastActions.createToast('alert', {
        title: `Reorder - Failure`,
        message: `Failed to reorder debts! Please try again later or refresh the page.`,
      })
    );
  }
}

export function* fetchLocalDebtsSaga() {
  try {
    const debts = yield call(fetchLocalDebts, DEFAULT_DEBTS);
    yield put(actions.replaceDebts(debts));
  } catch (e) {
    console.log(e);
  }
}

export function* debtsSaga() {
  yield all([
    takeEvery(actions.loadedDebts, checkDebtsSaga),
    takeEvery(actions.deleteDebt, deleteDebtSaga),
    takeEvery(actions.saveDebt, saveDebtSaga),
    takeEvery(actions.reorderDebts, reorderDebtsSaga),
    takeEvery([actions.addInterest, actions.addPayment], updateBalanceSaga),
    takeEvery(userActions.userUnauthenticated, fetchLocalDebtsSaga),
  ]);
}

// Reducer
export default handleActions(
  {
    [actions.addDebt](state) {

      return {
        ...state,
        debts: [
          ...state.debts,
          {
            ...DEBT_CONTRACT,
            id: uuid(),
            order: state.debts.length + 1,
            isNew: true,
          },
        ],
      };
    },
    [actions.removeDebt](state, { payload: id }) {
      return {
        ...state,
        debts: removeItem(state.debts, { id }),
      };
    },

    // Firebase
    [actions.loadedDebts](state, { payload: debtHash }) {
      if (!debtHash) return { ...state, loading: false };
      const debts = Object.keys(debtHash).map(id => normalizeDebt({ ...DEBT_CONTRACT, id, ...debtHash[id] }));
      return { ...state, debts, loading: false };
    },
    [actions.upsertDebt](state, { payload: debt }) {
      return {
        ...state,
        debts: replaceItem(state.debts, {
          ...normalizeDebt({ ...DEBT_CONTRACT, ...debt }),
        }),
      };
    },

    // Local Debts
    [actions.replaceDebts](state, { payload: debts }) {
      return { ...state, debts, debtsCache: debts, loading: false };
    },

    // Auth Change
    [userActions.userAuthenticated](state) {
      return { ...defaultState, debts: [] };
    },
    [userActions.userUnauthenticated](state) {
      return {
        ...defaultState,
        debts: DEFAULT_DEBTS,
        loading: false,
      };
    },
    [userActions.logoutSuccess](state) {
      return {
        ...defaultState,
        debts: state.debtsCache,
        loading: false,
      };
    },
  },
  defaultState
);



// WEBPACK FOOTER //
// ./src/modules/debts.js